<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon
} from 'front-library'
import {
  generateReportList,
  getReportRequest
} from '@/app/core/services/reports'
import {
  getFiscalRules,
  FiscalRule,
  deleteFiscalRule
} from '@/app/core/services/fiscal-rules'
import { usePayrollStore } from '@/store/payroll-data'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const storePayroll = usePayrollStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const acceptQuestion = ref()
const descriptionModal = ref()
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()
const tableFiscalRule = ref(null)
const FiscalRuleForDelete = ref()
const urlFile = ref(null)
const refresh = ref()
const payload = ref()

/* Datos componente table */
const columns = ref([
  { name: 'code', title: t('fiscalRules.list.code'), table: 'code' },
  { name: 'name', title: t('fiscalRules.list.name'), table: 'name' },
  ...(storePayroll.payroll?.country?.iso_code === 'MX'
    ? [
        {
          name: 'mxregymetype',
          title: t('fiscalRules.list.regimeType'),
          table: 'mxregymetype'
        },
        {
          name: 'mxpaymenttype',
          title: t('fiscalRules.list.paymentType'),
          table: 'mxpaymenttype'
        }
      ]
    : []),
  {
    name: 'updated_at',
    title: t('fiscalRules.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<FiscalRule[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createFiscalRule = () => {
  void router.push({ name: 'CreateFiscalRule' })
}
const updateFiscalRule = (id: any) => {
  void router.push({ name: 'UpdateFiscalRule', params: { id } })
}
const getFiscalRule = (id: any) => {
  void router.push({ name: 'GetFiscalRule', params: { id } })
}

/* Eliminar jornada */
const removeFiscalRule = (values: any) => {
  showModal.value = true
  titleModal.value = t('fiscalRules.list.actionStatus', {
    status: t('table.action.delete'),
    fiscalRule: values.name as string
  })
  descriptionModal.value = t('modal.description')
  acceptQuestion.value = t('modal.acceptQuestion', {
    action: t('table.action.delete')
  })
  FiscalRuleForDelete.value = values.id
}
const deleteConfirm = async () => {
  loading.value = true
  showModal.value = false
  await deleteFiscalRule(FiscalRuleForDelete.value)
    .then(response => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('fiscalRules.notification.action', [t('notification.wasRemoved')]),
        type: 'success',
        time: 10000
      })
      void fiscalRules()
    })
    .catch(async (error: any) => {
      notificationError(error, 'fiscalRules', t, notifications, loading)
    })
}

/* Generar reporte */
const downloadList = async () => {
  try {
    loading.value = true
    messageLoading.value = t('loading.report')
    urlFile.value = null
    const data = {
      code: 'configurations/entity-table',
      parameters: {
        query: JSON.stringify(payload.value),
        fields: payload.value.fields.filter(
          (field: any) => !field.includes('id')
        ),
        headers: columns.value
          .filter((item: any) => item.title !== '')
          .map((item: any) => item.title),
        module: t('fiscalRules.list.title', 1)
      }
    }
    const id = await generateReportList(data)
    refresh.value = setInterval(async () => {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        descriptionModal.value = response.errors
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    notificationError(error, 'fiscalRules', t, notifications, loading)
  }
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

/* Listar jornadas */
const fiscalRules = async () => {
  loading.value = true
  try {
    const data: any = await getFiscalRules(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value,
      storePayroll.payroll?.country?.iso_code
    )
    rows.value = data[0].results
    totalRows.value = data[0].totalItems
    payload.value = data[1]
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'fiscalRules', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <DModal
    :open="showModal"
    typeAlert="question"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="deleteConfirm" />
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('fiscalRules.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('fiscalRules.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="fiscalRules" />
          <DBtn @click.prevent="fiscalRules" v-html="t('form.button.search')" />
        </div>
        <div class="flex gap-2">
          <!-- <DBtn
            color="excel"
            type="button"
            @click="downloadList"
            v-html="t('form.button.download')" /> -->
          <DBtn
            v-if="store.hasPermission('fiscal_rules:create')"
            :icon="'PlusIcon'"
            type="button"
            @click="createFiscalRule()">
            {{
              t('form.button.create', {
                form: t('fiscalRules.list.title', 0)
              })
            }}
          </DBtn>
        </div>
      </div>
    </div>
    <DTable
      ref="tableFiscalRule"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), fiscalRules()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), fiscalRules()">
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('fiscal_rules:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getFiscalRule(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('fiscal_rules:update')"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateFiscalRule(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('fiscal_rules:delete')"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeFiscalRule(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
